/**
 * A ficha do paciente (#811, fase 1 da clínica).
 *
 * O que a clínica guarda sobre o cliente além do cadastro: nome social,
 * nascimento, endereço, profissão, estado civil, contato de emergência e a
 * preferência de contato. Só nas organizações com a funcionalidade "clinica"
 * ligada; sem ela, a rota responde 404.
 *
 * Fica de fora, por decisão de 14/10/2026, o que é dado de saúde (sexo,
 * gênero, exames, alertas), que espera a retenção do jurídico.
 */

import { z } from 'zod';

const UF = z.enum([
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
]);

const ESTADO_CIVIL = z.enum([
  'solteiro',
  'casado',
  'uniao_estavel',
  'separado',
  'divorciado',
  'viuvo',
]);

const CANAL = z.enum(['whatsapp', 'telefone', 'email']);
const HORARIO = z.enum(['manha', 'tarde', 'noite']);

/** Os campos da ficha, como a tela lê e como ela grava. */
const CamposDaFichaSchema = z.object({
  nome_social: z.string().max(120).nullable(),
  /** Data de calendário, `AAAA-MM-DD`. */
  nascimento: z
    .string()
    .regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/)
    .nullable(),
  endereco: z.object({
    /** Só os oito dígitos na resposta; a gravação aceita com traço. */
    cep: z.string().max(10).nullable(),
    logradouro: z.string().max(200).nullable(),
    numero: z.string().max(20).nullable(),
    complemento: z.string().max(100).nullable(),
    bairro: z.string().max(100).nullable(),
    cidade: z.string().max(100).nullable(),
    uf: UF.nullable(),
  }),
  profissao: z.string().max(100).nullable(),
  estado_civil: ESTADO_CIVIL.nullable(),
  /** Dado de terceiro, informado pelo paciente. */
  emergencia: z.object({
    nome: z.string().max(120).nullable(),
    telefone: z.string().max(40).nullable(),
  }),
  canal_preferido: CANAL.nullable(),
  horario_preferido: HORARIO.nullable(),
});

/**
 * A ficha de um cliente. Sem ficha preenchida, os campos vêm nulos e a
 * `versao` também: é o que a tela devolve ao salvar a primeira vez.
 */
export const FichaDoPacienteSchema = CamposDaFichaSchema.extend({
  versao: z
    .number()
    .int()
    .min(1)
    .nullable()
    .describe('A versão lida. Devolva em `versao` ao salvar; nula enquanto não há ficha.'),
});
export type FichaDoPaciente = z.infer<typeof FichaDoPacienteSchema>;

/**
 * Salvar a ficha inteira. Todo campo vai: `null` apaga. A `versao` é a que a
 * tela leu — gravar sobre outra devolve 409, porque outra pessoa salvou antes.
 */
export const SalvarFichaDoPacienteSchema = CamposDaFichaSchema.extend({
  versao: z.number().int().min(1).nullable(),
}).strict();
export type SalvarFichaDoPaciente = z.infer<typeof SalvarFichaDoPacienteSchema>;
